import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import { cleanDispose, eParse, empty, inform, isMobile, monetary } from "../../helpers";
import { dom, tool } from "../../fotastart";
import { STRINGS, uc, gc, dc } from "../../declarations";
import { $e } from "../../ojs.events";
import AddressPickerForm from "./AddressPickerForm";

const h = app("h");
const broker = app("broker");

ojs(AddressPickerForm);

export default class DeliveryRequestForm extends Component {
    holder = null;
    step = state(1);

    chosenPickup = state({});
    chosenDropoff = state({});

    recipientName = state("");
    recipientPhone = state("");
    
    paymentMethod = state("pod"); // pod, mpesa, vkm

    // Estimation details
    loadingFee = state(false);
    estimatedFee = state(null);
    distance = state(null);
    duration = state(null);
    feeError = state(null);
    
    vkmRequired = state(0);
    vkmBalance = state(0);
    canUseVkm = state(false);
    vkmReason = state("");

    constructor() {
        super();
        this.step.listener(this);
        this.chosenPickup.listener(this);
        this.chosenDropoff.listener(this);
        this.recipientName.listener(this);
        this.recipientPhone.listener(this);
        this.paymentMethod.listener(this);
        this.loadingFee.listener(this);
        this.estimatedFee.listener(this);
        this.distance.listener(this);
        this.duration.listener(this);
        this.feeError.listener(this);
        this.canUseVkm.listener(this);
    }

    $$deliveryRequest = {
        needs: {
            requestForm: (ed) => {
                this.showForm(ed);
            },
            closeRequestForm: () => this.closeForm(),
        },
        stored: () => {
            this.closeForm();
        },
        has: {
            estimatedFee: (ed) => {
                let { message } = eParse(ed);
                this.loadingFee.value = false;
                this.feeError.value = null;
                this.estimatedFee.value = message.estimated_fee;
                this.distance.value = message.distance;
                this.duration.value = message.duration;
                this.vkmRequired.value = message.vkm_required;
                this.vkmBalance.value = message.vkm_balance;
                this.canUseVkm.value = message.can_use_vkm;
                this.vkmReason.value = message.vkm_reason;
            },
            estimateError: (ed) => {
                let { message } = eParse(ed);
                this.loadingFee.value = false;
                this.feeError.value = message.message || "Failed to calculate delivery fee.";
            },
            storageError: () => {
                const btn = dom.get(`[hook="drf-submit-btn-${this.id}"]`);
                if (btn) {
                    dom.enable(btn);
                    dom.put("Place Request", btn);
                }
            }
        }
    };

    $$location = {
        is: {
            available: (ed) => {
                let { meta, message } = eParse(ed);
                if (meta.from === "DRF_PICKUP") {
                    this.chosenPickup.value = {
                        address: message.address,
                        latitude: message.latitude,
                        longitude: message.longitude
                    };
                    inform("Pickup location set!", STRINGS.SUCCESS);
                    this.recalculateFee();
                } else if (meta.from === "DRF_DROPOFF") {
                    this.chosenDropoff.value = {
                        address: message.address,
                        latitude: message.latitude,
                        longitude: message.longitude
                    };
                    inform("Dropoff location set!", STRINGS.SUCCESS);
                    this.recalculateFee();
                }
            }
        }
    };

    showForm(ed) {
        if (this.holder) {
            cleanDispose(this.holder);
        }

        this.resetForm();

        const formMarkup = () => this.renderForm();

        if (isMobile()) {
            this.holder = tool.offcanvas(
                h.DeliveryRequestCanvas(formMarkup(), {
                    class: "p-0 rounded-top-5",
                    style: "height: auto; max-height: 90vh;"
                })
            );
        } else {
            this.holder = tool.plainModal(
                h.DeliveryRequestModal(formMarkup())
            );
        }

        this.holder.show();
    }

    closeForm() {
        if (this.holder) {
            this.holder.hide();
        }
    }

    resetForm() {
        this.step.value = 1;
        this.chosenPickup.value = {};
        this.chosenDropoff.value = {};
        this.recipientName.value = gc.user?.first_name ?? "" + " " + gc.user?.last_name ?? "";
        this.recipientPhone.value = gc.user?.phone ?? "";
        this.paymentMethod.value = "pod";
        this.estimatedFee.value = null;
        this.distance.value = null;
        this.duration.value = null;
        this.feeError.value = null;
        this.canUseVkm.value = false;
    }

    openMap(type) {
        let location = {};
        if (type === "PICKUP" && !empty(this.chosenPickup.value)) {
            location.latitude = this.chosenPickup.value.latitude;
            location.longitude = this.chosenPickup.value.longitude;
        } else if (type === "DROPOFF" && !empty(this.chosenDropoff.value)) {
            location.latitude = this.chosenDropoff.value.latitude;
            location.longitude = this.chosenDropoff.value.longitude;
        }

        broker.send($e.location.is.needed, payload(location, { from: `DRF_${type}` }));
    }

    recalculateFee() {
        let pickupLat = this.chosenPickup.value?.latitude;
        let pickupLng = this.chosenPickup.value?.longitude;
        let dropoffLat = this.chosenDropoff.value?.latitude;
        let dropoffLng = this.chosenDropoff.value?.longitude;

        if (pickupLat && pickupLng && dropoffLat && dropoffLng) {
            this.loadingFee.value = true;
            this.estimatedFee.value = null;
            this.feeError.value = null;
            broker.send($e.deliveryRequest.needs.estimateFee, payload({
                pickup_lat: pickupLat,
                pickup_lng: pickupLng,
                dropoff_lat: dropoffLat,
                dropoff_lng: dropoffLng
            }, { from: "DRF" }));
        }
    }

    submitRequest() {
        const btn = dom.get(`[hook="drf-submit-btn-${this.id}"]`);
        if (btn) {
            dom.disable(btn);
            h.Spinner({ parent: btn, resetParent: true });
        }

        let pickupLat = this.chosenPickup.value.latitude;
        let pickupLng = this.chosenPickup.value.longitude;
        let dropoffLat = this.chosenDropoff.value.latitude;
        let dropoffLng = this.chosenDropoff.value.longitude;

        broker.send($e.deliveryRequest.needs.storage, payload({
            pickup_latitude: pickupLat,
            pickup_longitude: pickupLng,
            dropoff_latitude: dropoffLat,
            dropoff_longitude: dropoffLng,
            recipient_name: this.recipientName.value,
            recipient_number: this.recipientPhone.value,
            payment_method: this.paymentMethod.value
        }));
    }

    renderForm() {
        return h.div(
            { class: "p-3" },
            h.div(
                { class: "d-flex justify-content-between align-items-center mb-4" },
                h.h4({ class: "fw-bold mb-0 text-primary" }, "Request a Delivery"),
                h.button({
                    type: "button",
                    class: "btn-close",
                    listeners: { click: () => this.closeForm() }
                })
            ),
            this.renderProgress(),
            v(this.step, (stepVal) => {
                switch (stepVal.value) {
                    case 1:
                        return this.renderStep1();
                    case 2:
                        return this.renderStep2();
                    case 3:
                        return this.renderStep3();
                    case 4:
                        return this.renderStep4();
                    default:
                        return null;
                }
            })
        );
    }

    renderProgress() {
        return h.div(
            { class: "mb-4 text-center" },
            h.div(
                { class: "d-flex justify-content-center align-items-center gap-2 mb-2" },
                [1, 2, 3, 4].map((i) =>
                    v(this.step, (stepVal) =>
                        h.div(
                            {
                                class: `rounded-circle d-flex align-items-center justify-content-center fw-bold transition-all ${
                                    stepVal.value === i
                                        ? "bg-primary text-white shadow"
                                        : stepVal.value > i
                                        ? "bg-success text-white"
                                        : "bg-white text-muted border"
                                }`,
                                style: "width: 35px; height: 35px; font-size: 0.9rem;"
                            },
                            stepVal.value > i ? h.i({ class: "bi bi-check-lg" }) : i
                        )
                    )
                )
            ),
            v(this.step, (stepVal) =>
                h.h6(
                    {
                        class: "text-muted text-uppercase fw-bold small",
                        style: "font-size: 0.75rem; letter-spacing: 0.5px;"
                    },
                    `Step ${stepVal.value} of 4`
                )
            )
        );
    }

    renderStep1() {
        return h.div(
            { class: "animate-step" },
            h.h5({ class: "fw-bold mb-3 text-dark" }, "Select Pickup Location"),
            h.p({ class: "text-muted small mb-4" }, "Where should the delivery person pick up the package?"),

            v(this.chosenPickup, (pickup) => {
                if (empty(pickup.value)) {
                    return h.div(
                        {
                            class: "card border-primary border-2 bg-light shadow-sm mb-4 overflow-hidden",
                            onclick: this.method("openMap", ["PICKUP"]),
                            role: "button",
                            style: "border-style: dashed !important; cursor: pointer;",
                        },
                        h.div(
                            { class: "card-body text-center py-4" },
                            h.i({ class: "fas fa-map-marked-alt fa-2x text-primary mb-2" }),
                            h.h6({ class: "fw-bold text-dark mb-1" }, "Set Precise Pickup Location"),
                            h.span({ class: "text-muted small" }, "Tap to open map and pin the pickup address")
                        )
                    );
                }

                return h.div(
                    {
                        class: "card border-0 shadow-sm bg-white p-3 mb-4 d-flex flex-row align-items-center justify-content-between",
                        onclick: this.method("openMap", ["PICKUP"]),
                        role: "button",
                        style: "cursor: pointer;"
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-3 overflow-hidden" },
                        h.i({ class: "fas fa-map-pin text-primary fs-4" }),
                        h.div(
                            { class: "overflow-hidden" },
                            h.span({ class: "text-muted small d-block mb-0.5" }, "Pickup Location"),
                            h.div({ class: "fw-bold text-dark text-truncate small" }, pickup.value.address)
                        )
                    ),
                    h.i({ class: "fas fa-chevron-right text-muted ms-2" })
                );
            }),

            h.div(
                { class: "d-flex justify-content-end mt-4 pt-2" },
                h.button(
                    {
                        type: "button",
                        class: "btn btn-primary rounded-pill px-4 py-3",
                        listeners: {
                            click: () => {
                                if (empty(this.chosenPickup.value)) {
                                    inform("Please select a pickup location first.", STRINGS.ERROR);
                                    return;
                                }
                                this.step.value = 2;
                            }
                        }
                    },
                    "Next Step",
                    h.i({ class: "bi bi-arrow-right ms-2" })
                )
            )
        );
    }

    renderStep2() {
        return h.div(
            { class: "animate-step" },
            h.h5({ class: "fw-bold mb-3 text-dark" }, "Select Dropoff Location"),
            h.p({ class: "text-muted small mb-4" }, "Where should the package be delivered?"),

            v(this.chosenDropoff, (dropoff) => {
                if (empty(dropoff.value)) {
                    return h.div(
                        {
                            class: "card border-primary border-2 bg-light shadow-sm mb-4 overflow-hidden",
                            onclick: this.method("openMap", ["DROPOFF"]),
                            role: "button",
                            style: "border-style: dashed !important; cursor: pointer;",
                        },
                        h.div(
                            { class: "card-body text-center py-4" },
                            h.i({ class: "fas fa-map-marked-alt fa-2x text-primary mb-2" }),
                            h.h6({ class: "fw-bold text-dark mb-1" }, "Set Precise Dropoff Location"),
                            h.span({ class: "text-muted small" }, "Tap to open map and pin the dropoff address")
                        )
                    );
                }

                return h.div(
                    {
                        class: "card border-0 shadow-sm bg-white p-3 mb-4 d-flex flex-row align-items-center justify-content-between",
                        onclick: this.method("openMap", ["DROPOFF"]),
                        role: "button",
                        style: "cursor: pointer;"
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-3 overflow-hidden" },
                        h.i({ class: "fas fa-map-pin text-primary fs-4" }),
                        h.div(
                            { class: "overflow-hidden" },
                            h.span({ class: "text-muted small d-block mb-0.5" }, "Dropoff Location"),
                            h.div({ class: "fw-bold text-dark text-truncate small" }, dropoff.value.address)
                        )
                    ),
                    h.i({ class: "fas fa-chevron-right text-muted ms-2" })
                );
            }),

            v(this.loadingFee, (isLoading) => {
                if (isLoading.value) {
                    return h.div({ class: "text-center py-4 mb-4" }, h.Spinner());
                }

                return h.div(
                    {},
                    v(this.feeError, (err) => {
                        if (err.value) {
                            return h.div(
                                { class: "p-3 rounded-4 bg-danger bg-opacity-10 border border-danger-subtle text-danger mb-4 small fw-semibold" },
                                h.i({ class: "bi bi-exclamation-triangle-fill me-2" }),
                                err.value
                            );
                        }
                        return null;
                    }),
                    v(this.estimatedFee, (fee) => {
                        if (fee.value) {
                            return h.div(
                                { class: "p-3 rounded-4 border border-success-subtle bg-success bg-opacity-10 mb-4 d-flex justify-content-between align-items-center" },
                                h.div(
                                    {},
                                    h.div({ class: "small text-muted fw-bold" }, "Estimated Fee"),
                                    h.div({ class: "fw-bold text-success fs-5" }, monetary(fee.value, dc.userCurrency)),
                                    v(this.distance, (dist) => {
                                        if (dist.value === null) return null;
                                        const km = (dist.value / 1000).toFixed(1);
                                        return v(this.duration, (dur) => {
                                            const mins = Math.ceil((dur.value + 600) / 60);
                                            return h.small({ class: "text-muted d-block mt-1" }, `${km} KM • ~${mins} mins`);
                                        });
                                    })
                                ),
                                h.span({ class: "badge bg-success text-white px-3 py-2 rounded-pill" }, "Price Ready")
                            );
                        }
                        return null;
                    })
                );
            }),

            h.div(
                { class: "d-flex justify-content-between mt-4 pt-2" },
                h.button(
                    {
                        type: "button",
                        class: "btn btn-outline-secondary rounded-pill px-4 py-3",
                        listeners: { click: () => { this.step.value = 1; } }
                    },
                    h.i({ class: "bi bi-arrow-left me-2" }),
                    "Back"
                ),
                h.button(
                    {
                        type: "button",
                        class: "btn btn-primary rounded-pill px-4 py-3",
                        listeners: {
                            click: () => {
                                if (empty(this.chosenDropoff.value)) {
                                    inform("Please select a dropoff location first.", STRINGS.ERROR);
                                    return;
                                }

                                if (this.chosenPickup.value?.latitude === this.chosenDropoff.value?.latitude &&
                                    this.chosenPickup.value?.longitude === this.chosenDropoff.value?.longitude) {
                                    inform("Pickup and dropoff addresses must be different.", STRINGS.ERROR);
                                    return;
                                }

                                if (this.feeError.value) {
                                    inform(this.feeError.value, STRINGS.ERROR);
                                    return;
                                }

                                if (this.estimatedFee.value === null) {
                                    inform("Please wait for the fee to be estimated.", STRINGS.ERROR);
                                    return;
                                }

                                this.step.value = 3;
                            }
                        }
                    },
                    "Next Step",
                    h.i({ class: "bi bi-arrow-right ms-2" })
                )
            )
        );
    }

    renderStep3() {
        return h.div(
            { class: "animate-step" },
            h.h5({ class: "fw-bold mb-3 text-dark" }, "Recipient Information"),
            h.p({ class: "text-muted small mb-4" }, "Enter the contact details of the package recipient."),

            h.div(
                { class: "row g-3 mb-4" },
                h.div(
                    { class: "col-12" },
                    h.label({ class: "form-label small fw-bold text-secondary" }, "Recipient Full Name"),
                    h.input({
                        type: "text",
                        class: "form-control form-control-lg rounded-3 bg-light border-0 px-3",
                        placeholder: "e.g. John Doe",
                        value: this.recipientName.value,
                        listeners: { input: (e) => { this.recipientName.value = e.target.value; } }
                    })
                ),
                h.div(
                    { class: "col-12" },
                    h.label({ class: "form-label small fw-bold text-secondary" }, "Recipient Phone Number"),
                    h.input({
                        type: "tel",
                        class: "form-control form-control-lg rounded-3 bg-light border-0 px-3",
                        placeholder: "e.g. 0712345678",
                        value: this.recipientPhone.value,
                        listeners: { input: (e) => { this.recipientPhone.value = e.target.value; } }
                    })
                )
            ),

            h.div(
                { class: "d-flex justify-content-between gap-3 mt-4 pt-2" },
                h.button(
                    {
                        type: "button",
                        class: "btn btn-outline-secondary rounded-pill px-4 py-3",
                        listeners: { click: () => { this.step.value = 2; } }
                    },
                    h.i({ class: "bi bi-arrow-left me-2" }),
                    "Back"
                ),
                h.button(
                    {
                        type: "button",
                        class: "btn btn-primary rounded-pill px-4 py-3",
                        listeners: {
                            click: () => {
                                if (!this.recipientName.value.trim()) {
                                    inform("Recipient name is required.", STRINGS.ERROR);
                                    return;
                                }
                                if (!this.recipientPhone.value.trim()) {
                                    inform("Recipient phone number is required.", STRINGS.ERROR);
                                    return;
                                }
                                this.step.value = 4;
                            }
                        }
                    },
                    "Next Step",
                    h.i({ class: "bi bi-arrow-right ms-2" })
                )
            )
        );
    }

    renderStep4() {
        return h.div(
            { class: "animate-step" },
            h.h5({ class: "fw-bold mb-3 text-dark" }, "Payment Options"),
            h.p({ class: "text-muted small mb-4" }, "Choose how you want to pay for the delivery service."),

            h.h6({ class: "fw-bold small text-secondary mb-3" }, "Select Payment Option"),
            h.div(
                { class: "d-flex flex-column gap-2 mb-4" },
                
                // POD
                v(this.paymentMethod, (method) =>
                    h.button(
                        {
                            class: `btn p-3 rounded-4 text-start border-2 transition-all d-flex align-items-center justify-content-between text-dark ${
                                method.value === "pod"
                                    ? "bg-primary-subtle border-primary"
                                    : "bg-white border-light-subtle"
                            }`,
                            listeners: { click: () => { this.paymentMethod.value = "pod"; } }
                        },
                        h.div(
                            {},
                            h.div({ class: "fw-bold" }, "Pay on Delivery"),
                            h.small({ class: "text-muted" }, "Pay monetary value to deliverer upon arrival")
                        ),
                        method.value === "pod"
                            ? h.i({ class: "bi bi-check-circle-fill text-primary fs-5" })
                            : h.i({ class: "bi bi-circle text-muted fs-5" })
                    )
                ),

                // Mpesa
                v(this.paymentMethod, (method) =>
                    h.button(
                        {
                            class: `btn p-3 rounded-4 text-start border-2 transition-all d-flex align-items-center justify-content-between text-dark ${
                                method.value === "mpesa"
                                    ? "bg-primary-subtle border-primary"
                                    : "bg-white border-light-subtle"
                            }`,
                            listeners: { click: () => { this.paymentMethod.value = "mpesa"; } }
                        },
                        h.div(
                            {},
                            h.div({ class: "fw-bold" }, "Pay Now"),
                            h.small({ class: "text-muted" }, "Pay now using the available payment method")
                        ),
                        method.value === "mpesa"
                            ? h.i({ class: "bi bi-check-circle-fill text-primary fs-5" })
                            : h.i({ class: "bi bi-circle text-muted fs-5" })
                    )
                ),

                // Subscription VKM
                v(this.canUseVkm, (canUse) =>
                    v(this.paymentMethod, (method) => {
                        const isSelected = method.value === "vkm";
                        const isDisabled = !canUse.value;

                        return h.button(
                            {
                                class: `btn p-3 rounded-4 text-start border-2 transition-all d-flex align-items-center justify-content-between text-dark ${
                                    isDisabled ? "btn-light border-light text-muted opacity-50" :
                                    isSelected
                                        ? "bg-primary-subtle border-primary"
                                        : "bg-white border-light-subtle"
                                }`,
                                listeners: {
                                    click: () => {
                                        if (isDisabled) {
                                            inform(`Cannot use vKM: ${this.vkmReason.value.replace("_", " ")}`, STRINGS.ERROR);
                                            return;
                                        }
                                        this.paymentMethod.value = "vkm";
                                    }
                                }
                            },
                            h.div(
                                {},
                                h.div(
                                    { class: "fw-bold" },
                                    "Pay with vKM ",
                                    h.span({ class: "badge bg-info text-white font-monospace text-xs" }, `${this.vkmRequired.value} vKM`)
                                ),
                                h.small(
                                    { class: "text-muted" },
                                    `Uses subscription tokens. Balance: ${this.vkmBalance.value.toFixed(1)} vKM`
                                )
                            ),
                            isDisabled ? h.i({ class: "bi bi-slash-circle text-muted fs-5" }) :
                            isSelected
                                ? h.i({ class: "bi bi-check-circle-fill text-primary fs-5" })
                                : h.i({ class: "bi bi-circle text-muted fs-5" })
                        );
                    })
                )
            ),

            h.div(
                { class: "d-flex justify-content-between gap-3 mt-4 pt-2" },
                h.button(
                    {
                        type: "button",
                        class: "btn btn-outline-secondary rounded-pill px-4 py-3",
                        listeners: { click: () => { this.step.value = 3; } }
                    },
                    h.i({ class: "bi bi-arrow-left me-2" }),
                    "Back"
                ),
                h.button(
                    {
                        type: "button",
                        class: "btn btn-success rounded-pill px-4 py-3 flex-fill fw-bold",
                        hook: `drf-submit-btn-${this.id}`,
                        listeners: {
                            click: () => {
                                this.submitRequest();
                            }
                        }
                    },
                    "Place Request"
                )
            )
        );
    }
}

export function DeliveryRequestCanvas(...args) {
    return h.$(h.div({ class: "offcanvas-body p-4" }, ...args));
}

export function DeliveryRequestModal(...args) {
    return h.$(
        h.div(
            { class: "modal-content border-0 rounded-4 shadow-lg" },
            h.div({ class: "modal-body p-4" }, ...args)
        )
    );
}

ojs(DeliveryRequestForm, DeliveryRequestCanvas, DeliveryRequestModal);
